/**
 * Value formatting utilities for warehouse table system
 * 
 * Provides consistent formatting across all warehouse modules
 * following Norwegian locale standards where applicable.
 */

/**
 * Format number with Norwegian locale (space as thousand separator)
 */
export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('no-NO');
};

/**
 * Format currency in NOK with Norwegian locale
 */
export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('no-NO', {
    style: 'currency',
    currency: 'NOK',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Format date to Norwegian format (dd.mm.yyyy)
 */
export const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return '-';
  
  const date = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString('no-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Format datetime to Norwegian format (dd.mm.yyyy HH:mm)
 */
export const formatDateTime = (value: string | Date | null | undefined): string => {
  if (!value) return '-';
  
  const date = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString('no-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format boolean as Ja/Nei (Norwegian Yes/No)
 */
export const formatBoolean = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return value ? 'Ja' : 'Nei';
};

/**
 * Format percentage with 1 decimal place
 */
export const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return `${(value * 100).toFixed(1)}%`;
};

/**
 * Format file size in bytes to human readable format
 */
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined) return '-';
  
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

/**
 * Format quantity with unit (e.g., "100 stk")
 */
export const formatQuantity = (
  value: number | null | undefined,
  unit: string = 'stk'
): string => {
  if (value === null || value === undefined) return '-';
  return `${formatNumber(value)} ${unit}`;
};

/**
 * Format status with color coding
 */
export const formatStatus = (
  value: string | null | undefined,
  statusMap?: Record<string, { label: string; color: string }>
): string => {
  if (!value) return '-';
  
  if (statusMap && statusMap[value]) {
    return statusMap[value].label;
  }
  
  // Default status formatting - capitalize first letter
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

/**
 * Truncate text to specified length with ellipsis
 */
export const truncateText = (
  value: string | null | undefined,
  maxLength: number = 50
): string => {
  if (!value) return '-';
  
  if (value.length <= maxLength) return value;
  return value.substring(0, maxLength - 3) + '...';
};

/**
 * Format phone number (Norwegian format)
 */
export const formatPhoneNumber = (value: string | null | undefined): string => {
  if (!value) return '-';
  
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Format Norwegian phone numbers
  if (digits.length === 8) {
    return `${digits.substring(0, 3)} ${digits.substring(3, 5)} ${digits.substring(5)}`;
  } else if (digits.length === 11 && digits.startsWith('47')) {
    return `+47 ${digits.substring(2, 5)} ${digits.substring(5, 7)} ${digits.substring(7)}`;
  }
  
  return value; // Return original if not standard format
};

/**
 * Format postal code (Norwegian format)
 */
export const formatPostalCode = (value: string | null | undefined): string => {
  if (!value) return '-';
  
  const digits = value.replace(/\D/g, '');
  if (digits.length === 4) {
    return digits;
  }
  
  return value; // Return original if not 4 digits
};

/**
 * Format weight in kg with proper Norwegian formatting
 */
export const formatWeight = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return `${formatNumber(value)} kg`;
};

/**
 * Format volume in liters or cubic meters
 */
export const formatVolume = (
  value: number | null | undefined,
  unit: 'L' | 'm³' = 'L'
): string => {
  if (value === null || value === undefined) return '-';
  return `${formatNumber(value)} ${unit}`;
};

/**
 * Format temperature in Celsius
 */
export const formatTemperature = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(1)}°C`;
};

/**
 * Default formatter - handles common types automatically
 */
export const defaultFormatter = (
  value: any,
  type: 'text' | 'number' | 'date' | 'currency' | 'boolean' | 'status'
): string => {
  switch (type) {
    case 'number':
      return formatNumber(value);
    case 'currency':
      return formatCurrency(value);
    case 'date':
      return formatDate(value);
    case 'boolean':
      return formatBoolean(value);
    case 'status':
      return formatStatus(value);
    case 'text':
    default:
      return value?.toString() ?? '';
  }
};